import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

type Preset = 'today' | '7d' | '30d' | '90d';

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function daysBetween(start: string, end: string): number {
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)));
}

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '16px',
};

const cardTitle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '12px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text-muted)',
  fontSize: '12px',
  fontWeight: 600,
  background: 'var(--surface2)',
};

const tdStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text)',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontSize: '13px',
  fontFamily: 'inherit',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  border: 'none',
  borderRadius: '8px',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  background: 'var(--surface2)',
  color: 'var(--text)',
  fontSize: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const SOURCE_LABELS: Record<string, string> = { api: 'UI', slack: 'Slack', mcp: 'Claude / Copilot' };
// Daily Activity stacks one colour per source, bottom to top in this order.
const SOURCE_COLORS: [string, string][] = [
  ['api', 'var(--series-1)'],
  ['slack', 'var(--series-2)'],
  ['mcp', 'var(--series-3)'],
  ['other', 'var(--text-muted)'],
];

interface McpUsage {
  days: number;
  total_calls: number;
  active_users: number;
  error_rate: number;
  est_cost_usd: number;
  top_users: { user_id: string; user_name: string | null; calls: number; queries: number; active_days: number; last_seen: string | null }[];
  tools: { tool: string; calls: number }[];
  datasets: { dataset: string; calls: number }[];
  clients: { client: string; calls: number }[];
  daily: { date: string; calls: number; queries: number }[];
}

function CountTable({ title, rows, label }: { title: string; rows: { name: string; calls: number }[]; label: string }) {
  return (
    <div style={card}>
      <div style={cardTitle}>{title}</div>
      {!rows.length ? (
        <Empty text="No data" />
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{label}</th>
              <th style={thStyle}>Calls</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td style={tdStyle}>{r.name}</td>
                <td style={tdStyle}>{r.calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ textAlign: 'center', padding: '28px', color: 'var(--text-muted)', fontSize: '13px' }}>{text}</div>;
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px' }}>
      <div style={{ fontSize: '34px', fontWeight: 700, color: 'var(--accent)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
    </div>
  );
}

/** Live analytics dashboard — React port of the former static/analytics.html */
export default function AnalyticsDashboard({ compact = false }: { compact?: boolean }) {
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDate(d);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(formatDate(today));
  const [appliedStart, setAppliedStart] = useState(defaultStart);
  const [appliedEnd, setAppliedEnd] = useState(formatDate(today));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number | null>(null);
  const [avgSeconds, setAvgSeconds] = useState<number | null>(null);
  const [dailyRows, setDailyRows] = useState<{ activity_date: string; source: string; total_questions: number }[]>([]);
  const [popularDatasets, setPopularDatasets] = useState<any[]>([]);
  const [topUsers, setTopUsers] = useState<any[]>([]);
  const [channelActivity, setChannelActivity] = useState<any[]>([]);
  const [configChanges, setConfigChanges] = useState<any[]>([]);
  const [mcpUsage, setMcpUsage] = useState<McpUsage | null>(null);
  const [mcpError, setMcpError] = useState('');

  const days = daysBetween(appliedStart, appliedEnd);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const chartDays = Math.min(days, 30);
    try {
      const [
        activeRes,
        totalRes,
        avgRes,
        dailyRes,
        popularRes,
        topRes,
        channelRes,
        configRes,
      ] = await Promise.all([
        api.get('/api/analytics/active-users'),
        api.get(`/api/analytics/total-questions?days=${days}`),
        api.get('/api/analytics/avg-response-time'),
        api.get(`/api/analytics/daily-activity?days=${chartDays}`),
        api.get(`/api/analytics/popular-datasets?limit=10&days=${days}`),
        api.get(`/api/analytics/top-users?limit=15&days=${days}`),
        api.get(`/api/analytics/channel-activity?days=${days}`),
        api.get('/api/analytics/config-changes?limit=20'),
      ]);

      setActiveUsers(activeRes.data.active_users ?? 0);
      setTotalQuestions(totalRes.data.total ?? 0);
      setAvgSeconds(avgRes.data.avg_seconds ?? null);
      setDailyRows(dailyRes.data.rows || []);
      setPopularDatasets(popularRes.data.rows || []);
      setTopUsers(topRes.data.rows || []);
      setChannelActivity(channelRes.data.rows || []);
      setConfigChanges(configRes.data.rows || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load analytics');
    }
    // Separate call: MCP usage comes from the operational DB, not BigQuery.
    try {
      setMcpError('');
      const mcpRes = await api.get(`/api/analytics/mcp-usage?days=${days}`);
      setMcpUsage(mcpRes.data);
    } catch (err: any) {
      setMcpUsage(null);
      setMcpError(err?.response?.data?.detail || 'Could not load Claude / Copilot usage.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilter = () => {
    if (!startDate || !endDate) {
      setError('Select both start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const setPreset = (preset: Preset) => {
    const end = new Date();
    const start = new Date();
    if (preset === '7d') start.setDate(start.getDate() - 7);
    if (preset === '30d') start.setDate(start.getDate() - 30);
    if (preset === '90d') start.setDate(start.getDate() - 90);
    const s = formatDate(start);
    const e = formatDate(end);
    setStartDate(s);
    setEndDate(e);
    setAppliedStart(s);
    setAppliedEnd(e);
  };

  const chartData = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    // Claude / Copilot comes from the MCP audit log (queries per day), not chat_logs,
    // where its ask_analyst rows would otherwise count twice.
    dailyRows.forEach((r) => {
      if (r.source === 'mcp') return;
      const day = (map[r.activity_date] ||= {});
      const src = SOURCE_COLORS.some(([k]) => k === r.source) ? r.source : 'other';
      day[src] = (day[src] || 0) + r.total_questions;
    });
    (mcpUsage?.daily || []).forEach((r) => {
      if (r.queries) (map[r.date] ||= {}).mcp = r.queries;
    });
    const out: { activity_date: string; total_questions: number; bySource: Record<string, number> }[] = [];
    const cursor = new Date(appliedStart);
    const end = new Date(appliedEnd);
    const maxDays = 30;
    let n = 0;
    for (let d = new Date(cursor); d <= end && n < maxDays; d.setDate(d.getDate() + 1), n++) {
      const key = formatDate(d);
      const bySource = map[key] || {};
      const total = Object.values(bySource).reduce((a, b) => a + b, 0);
      out.push({ activity_date: key, total_questions: total, bySource });
    }
    return out;
  }, [dailyRows, mcpUsage, appliedStart, appliedEnd]);

  const maxQuestions = Math.max(...chartData.map((r) => r.total_questions), 1);
  const shownSources = SOURCE_COLORS.filter(([k]) => chartData.some((r) => r.bySource[k]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '14px' : '18px' }}>
      <div style={{
        ...card,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '12px 14px',
      }}>
        <label style={{ fontSize: '13px', fontWeight: 600 }}>Date range</label>
        <input type="date" style={inputStyle} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>to</span>
        <input type="date" style={inputStyle} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <button type="button" style={btnPrimary} onClick={applyFilter}>Apply</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {([
            ['today', 'Today'],
            ['7d', 'Last 7 Days'],
            ['30d', 'Last 30 Days'],
            ['90d', 'Last 90 Days'],
          ] as [Preset, string][]).map(([id, label]) => (
            <button key={id} type="button" style={btnGhost} onClick={() => setPreset(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px 12px',
          borderRadius: '8px',
          background: 'rgba(239,68,68,0.12)',
          color: 'var(--red)',
          fontSize: '13px',
        }}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '14px',
      }}>
        <div style={card}>
          <div style={cardTitle}>Active Users</div>
          {loading && activeUsers === null ? <Empty text="Loading…" /> : <Metric value={String(activeUsers ?? 0)} label="Today" />}
        </div>
        <div style={card}>
          <div style={cardTitle}>Total Questions</div>
          {loading && totalQuestions === null ? <Empty text="Loading…" /> : <Metric value={String(totalQuestions ?? 0)} label={`${days} Days`} />}
        </div>
        <div style={card}>
          <div style={cardTitle}>Avg Response Time</div>
          {loading && avgSeconds === null ? (
            <Empty text="Loading…" />
          ) : (
            <Metric value={avgSeconds != null ? `${avgSeconds.toFixed(1)}s` : 'N/A'} label="Last 7 Days" />
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={cardTitle}>Daily Activity</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginLeft: 'auto' }}>
            {shownSources.map(([k, color]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color }} />
                {SOURCE_LABELS[k] || 'Other'}
              </span>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <Empty text={loading ? 'Loading…' : 'No data'} />
        ) : (
          <div style={{
            minHeight: '220px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '6px',
            padding: '24px 0 36px',
            overflowX: 'auto',
          }}>
            {chartData.map((row) => {
              const height = Math.max((row.total_questions / maxQuestions) * 180, 8);
              const label = new Date(row.activity_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return (
                <div
                  key={row.activity_date}
                  title={`${label}: ${row.total_questions} questions${row.total_questions ? ' — ' + SOURCE_COLORS
                    .filter(([k]) => row.bySource[k])
                    .map(([k]) => `${SOURCE_LABELS[k] || 'Other'} ${row.bySource[k]}`)
                    .join(' · ') : ''}`}
                  style={{
                    flex: '1 0 18px',
                    height: `${height}px`,
                    background: row.total_questions ? 'transparent' : 'var(--surface2)',
                    borderRadius: '4px 4px 0 0',
                    position: 'relative',
                    minWidth: '18px',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    gap: '2px',
                    overflow: 'visible',
                  }}
                >
                  {SOURCE_COLORS.filter(([k]) => row.bySource[k]).map(([k, color], i, shown) => (
                    <div
                      key={k}
                      style={{
                        flex: `${row.bySource[k]} 0 0`,
                        background: color,
                        borderRadius: i === shown.length - 1 ? '4px 4px 0 0' : 0,
                      }}
                    />
                  ))}
                  <div style={{
                    position: 'absolute',
                    top: '-18px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}>
                    {row.total_questions}
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-22px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={cardTitle}>Popular Datasets</div>
        {!popularDatasets.length ? (
          <Empty text={loading ? 'Loading…' : 'No data'} />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Dataset</th>
                <th style={thStyle}>Questions</th>
                <th style={thStyle}>Users</th>
                <th style={thStyle}>Avg Time</th>
              </tr>
            </thead>
            <tbody>
              {popularDatasets.map((row, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{row.dataset_hint}</td>
                  <td style={tdStyle}>{row.question_count}</td>
                  <td style={tdStyle}>{row.unique_users}</td>
                  <td style={tdStyle}>{row.avg_response_time_sec != null ? `${row.avg_response_time_sec}s` : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={card}>
        <div style={cardTitle}>Top Users</div>
        {!topUsers.length ? (
          <Empty text={loading ? 'Loading…' : 'No data'} />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Sessions</th>
                <th style={thStyle}>Questions</th>
                <th style={thStyle}>Active Days</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((row, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{row.user_name || row.user_id}</td>
                  <td style={tdStyle}>{SOURCE_LABELS[row.source] || row.source}</td>
                  <td style={tdStyle}>{row.sessions}</td>
                  <td style={tdStyle}>{row.questions}</td>
                  <td style={tdStyle}>{row.active_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ ...cardTitle, marginBottom: 0, marginTop: '6px' }}>Claude / Copilot (Pearson MCP)</div>
      {mcpError && (
        <div style={{
          padding: '10px 12px',
          borderRadius: '8px',
          background: 'rgba(239,68,68,0.12)',
          color: 'var(--red)',
          fontSize: '13px',
        }}>
          {mcpError}
        </div>
      )}
      {mcpUsage && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '14px',
          }}>
            <div style={card}>
              <div style={cardTitle}>Calls</div>
              <Metric value={String(mcpUsage.total_calls)} label={`${mcpUsage.days} Days`} />
            </div>
            <div style={card}>
              <div style={cardTitle}>Users</div>
              <Metric value={String(mcpUsage.active_users)} label={`${mcpUsage.days} Days`} />
            </div>
            <div style={card}>
              <div style={cardTitle}>Error Rate</div>
              <Metric value={`${(mcpUsage.error_rate * 100).toFixed(1)}%`} label="Rejected or failed" />
            </div>
            <div style={card}>
              <div style={cardTitle}>Est. BigQuery Cost</div>
              <Metric value={`$${mcpUsage.est_cost_usd.toFixed(2)}`} label="On-demand pricing" />
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>Top Users</div>
            {!mcpUsage.top_users.length ? (
              <Empty text="No Claude / Copilot activity" />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Calls</th>
                    <th style={thStyle}>Questions</th>
                    <th style={thStyle}>Active Days</th>
                    <th style={thStyle}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {mcpUsage.top_users.slice(0, 15).map((row) => (
                    <tr key={row.user_id}>
                      <td style={tdStyle}>{row.user_name || row.user_id}</td>
                      <td style={tdStyle}>{row.calls}</td>
                      <td style={tdStyle}>{row.queries}</td>
                      <td style={tdStyle}>{row.active_days}</td>
                      <td style={tdStyle}>{row.last_seen ? new Date(row.last_seen).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '14px',
          }}>
            <CountTable title="Datasets" label="Dataset" rows={mcpUsage.datasets.map((r) => ({ name: r.dataset, calls: r.calls }))} />
            <CountTable title="Tools" label="Tool" rows={mcpUsage.tools.map((r) => ({ name: r.tool, calls: r.calls }))} />
            <CountTable title="Clients" label="Client" rows={mcpUsage.clients.map((r) => ({ name: r.client, calls: r.calls }))} />
          </div>
        </>
      )}

      <div style={card}>
        <div style={cardTitle}>Slack Channel Activity</div>
        {!channelActivity.length ? (
          <Empty text={loading ? 'Loading…' : 'No Slack activity'} />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Channel</th>
                <th style={thStyle}>Users</th>
                <th style={thStyle}>Questions</th>
                <th style={thStyle}>Conversations</th>
              </tr>
            </thead>
            <tbody>
              {channelActivity.map((row, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{row.channel_name}</td>
                  <td style={tdStyle}>{row.active_users}</td>
                  <td style={tdStyle}>{row.questions}</td>
                  <td style={tdStyle}>{row.conversations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={card}>
        <div style={cardTitle}>Recent Config Changes</div>
        {!configChanges.length ? (
          <Empty text={loading ? 'Loading…' : 'No config changes'} />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Resource</th>
                <th style={thStyle}>User</th>
              </tr>
            </thead>
            <tbody>
              {configChanges.map((row, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{row.change_time ? new Date(row.change_time).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>{row.action}</td>
                  <td style={tdStyle}>{row.resource_type}</td>
                  <td style={tdStyle}>{row.resource_id || 'N/A'}</td>
                  <td style={tdStyle}>{row.actor_email || 'system'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
